import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Subject, Subscription } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { CustomTableSelectComponent } from 'src/app/components/custom-table-select/custom-table-select.component';
import { ActionColor, CheckBoxAction, TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { OptionWindowModel } from 'src/app/models/authentication/option-window.model';
import { UserModel } from 'src/app/models/authentication/user.model';
import { CostAdjustmentDetailModel } from 'src/app/models/inventory/cost-adjustment-detail.model';
import { CostAdjustmentModel } from 'src/app/models/inventory/cost-adjustment.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { ArticleSelectionByEntryTableModel } from 'src/app/models/tables/archive/article-selection-by-entry-table.model';
import { CostAdjustmentDetailCaptureTableModel } from 'src/app/models/tables/inventory/cost-adjustment-detail-capture-table.model';
import { CostAdjustmentDetailService } from 'src/app/services/inventory/cost-adjustment-detail.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-cost-adjustment-detail',
  templateUrl: './cost-adjustment-detail.component.html',
  styleUrls: ['./cost-adjustment-detail.component.scss'],
  standalone: false
})
export class CostAdjustmentDetailComponent implements OnInit {

  @ViewChild('articleSelection') articleSelectionModalComponent?: CustomModalComponent | null = null;

  @Input() costAdjustmentModel: CostAdjustmentModel = new CostAdjustmentModel();
  @Input() disabledDetail: boolean = false;
  @Input() permissionsUser: OptionWindowModel[] = [];
  @Input() permissionUpdatable: boolean = false;
  @Input() userAuthenticated: UserModel = new UserModel();
  @Output() emitQuantityDetails = new EventEmitter<number>();
  @Output() hasAdjustedUnitCostInZero = new EventEmitter<boolean>();

  ascendant: boolean = true;
  ascendantArticle: boolean = true;
  checkboxActions: CheckBoxAction[] = [
    {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      color: ActionColor.DANGER,
      action: 'bulk-delete'
    },
  ];
  clearSelectionTrigger: number = 0;
  columnSort: string | null = null;
  columnSortArticle: string | null = null;
  componentTableSelection = CustomTableSelectComponent;
  costAdjustmentDetail: CostAdjustmentDetailModel = new CostAdjustmentDetailModel();
  costAdjustmentDetails: CostAdjustmentDetailModel[] = [];
  dataArticle: PagedResponseModel<ArticleSelectionByEntryTableModel> =
    new PagedResponseModel<ArticleSelectionByEntryTableModel>();
  detailData: PagedResponseModel<CostAdjustmentDetailCaptureTableModel> =
    new PagedResponseModel<CostAdjustmentDetailCaptureTableModel>();
  detailHeader: ColumnResponseModel[] = [];
  filtersArticle: ColumnResponseModel[] = [];
  headerArticle: ColumnResponseModel[] = [];
  inputsForArticlesSelection$ = new BehaviorSubject<any>({
    headers: [],
    data: [],
    showCheckbox: false,
    showRecover: true,
    isMultipleSelect: true,
    filters: [],
  });
  isFormView: boolean = false;
  limit: number = 10;
  limitArticle: number = 10;
  output$ = new Subject<any>();
  page: number = 1;
  pageArticle: number = 1;
  quantityDetails: number = 0;
  subscription: Subscription;
  tableActions: TableAction[] = [];

  constructor(private costAdjustmentDetailService: CostAdjustmentDetailService,
    private toastHandlerService: ToastHandlerService) { }

  async ngOnInit() {
    await this.getColumnsForCapture();
    this.tableActions = this.getDefaultActions();
    this.subscription = this.output$.subscribe(({ type, payload }) => {
      switch (type) {
        case 'onPageChange':
          this.onPageChangeArticle(payload);
          break;
        case 'onFilterChange':
          this.onFilterChange(payload);
          break;
        default:
          console.warn('Tipo de evento desconocido:', type);
      }
    });
  }

  async ngOnChanges(changes: SimpleChanges) {
    const disabledDetail = changes["disabledDetail"];
    const costAdjustmentModel = changes["costAdjustmentModel"];
    if (costAdjustmentModel && !costAdjustmentModel.firstChange) {
      this.clearSelectionTrigger++;
      await this.getCostAdjustmentDetailsForCapture();
      this.costAdjustmentDetail = new CostAdjustmentDetailModel();
    }
    if (disabledDetail && !disabledDetail.firstChange) {
      this.tableActions = this.getDefaultActions();
    }
  }

  async deleteCostAdjustmentDetail(selectedIds: number[]) {
    if (selectedIds.length > 0) {
      const result = await Swal.fire({
        title: 'Atención',
        text: "¿Realmente desea eliminar el detalle del ajuste.?",
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Si',
        cancelButtonText: 'No'
      });
      if (result.isConfirmed) {
        try {
          await firstValueFrom(this.costAdjustmentDetailService.deleteSomeDetails(selectedIds));
          this.toastHandlerService.showToast('success', 'El detalle del ajuste fue eliminado exitosamente.');
          this.clearSelectionTrigger++;
          await this.getCostAdjustmentDetailsForCapture();
        } catch (error) {
          console.error('Error al eliminar el detalle del ajuste.', error);
        }
      }
    }
  }

  async editCostAdjustmentDetail(id: number) {
    this.costAdjustmentDetail = await this.getCostAdjustmentDetailById(id);
    this.getFormView(true);
  }

  async getArticlesByEntry() {
    try {
      const inventoryEntryId = this.costAdjustmentModel.inventoryEntryId;
      const response = await firstValueFrom(this.costAdjustmentDetailService.getArticlesByEntry(inventoryEntryId,
        this.pageArticle, this.limitArticle, this.columnSortArticle, this.ascendantArticle, this.filtersArticle));
      this.dataArticle = response;
      this.updateInputs();
    } catch (error) {
      console.error('Error al obtener la lista de los artículos', error);
    }
  }

  async getColumnsForArticle() {
    try {
      if (this.headerArticle.length === 0) {
        const response = await firstValueFrom(this.costAdjustmentDetailService.getColumnsForArticle());
        this.headerArticle = response;
        this.updateInputs();
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la selección de artículos.', error);
    }
  }

  async getColumnsForCapture() {
    try {
      if (this.detailHeader.length === 0) {
        const response = await firstValueFrom(this.costAdjustmentDetailService.getColumnsForCaptureTable());
        this.detailHeader = response;
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta.', error);
    }
  }

  async getCostAdjustmentDetailsForCapture() {
    try {
      const costAdjustmentId = this.costAdjustmentModel.costAdjustmentId;
      const response = await firstValueFrom(this.costAdjustmentDetailService.getCostAdjustmentDetailsForCaptureTable(this.page,
        this.limit, this.columnSort, this.ascendant, costAdjustmentId));
      this.getFormView(false);
      this.detailData = response;
      this.quantityDetails = this.detailData.totalItems;
      this.emitQuantityDetails.emit(this.quantityDetails);
      this.hasAdjustedUnitCostInZero.emit(this.detailData.items.some(detail => detail.adjustedUnitCost === 0));
    } catch (error) {
      console.error('Error al buscar los detalles del ajuste.', error);
    }
  }

  async getCostAdjustmentDetailById(costAdjustmentDetailId: number): Promise<CostAdjustmentDetailModel> {
    try {
      const response = await firstValueFrom(this.costAdjustmentDetailService.getCostAdjustmentDetailById(costAdjustmentDetailId));
      this.getFormView(true);
      return response;
    } catch (error) {
      console.error('Error al obtener el detalle por id.', error);
      return new CostAdjustmentDetailModel();
    }
  }

  async getCostAdjustmentDetailByKeys(articleId: number): Promise<CostAdjustmentDetailModel> {
    try {
      const costAdjustmentId = this.costAdjustmentModel.costAdjustmentId;
      const response = await firstValueFrom(this.costAdjustmentDetailService.getCostAdjustmentDetailByKeys(costAdjustmentId, articleId));
      this.getFormView(true);
      return response;
    } catch (error) {
      console.error('Error al obtener el detalle del traslado.', error);
      return new CostAdjustmentDetailModel();
    }
  }

  getDefaultActions(): TableAction[] {
    if (this.disabledDetail) {
      return [];
    } else {
      return [
        {
          label: 'Editar',
          icon: 'pi pi-pencil',
          action: 'edit'
        }
      ];
    }
  }

  getFormView(isFormView: boolean) {
    this.isFormView = isFormView;
  }

  async handleSelectedItem(selectedItem: any) {
    if (selectedItem.length == 1) {
      const [{ articleId, quantity }] = selectedItem;
      const response = await this.getCostAdjustmentDetailByKeys(articleId);
      if (response.costAdjustmentDetailId === 0) {
        response.articleId = articleId;
        response.quantity = quantity;
        this.costAdjustmentDetail = response;
      } else {
        this.costAdjustmentDetail = response;
      }
    } else {
      const newDetails = selectedItem.map((element: {
        quantity: number,
        articleId: number,
      }) => {
        const newCostAdjustmentDetail = new CostAdjustmentDetailModel();
        const costAdjustmentId = this.costAdjustmentModel.costAdjustmentId;
        newCostAdjustmentDetail.costAdjustmentId = costAdjustmentId
        newCostAdjustmentDetail.articleId = element.articleId;
        newCostAdjustmentDetail.quantity = element.quantity;
        return newCostAdjustmentDetail;
      });
      this.costAdjustmentDetails = newDetails;
    }
  }

  async onAction(event: { action: string, item?: CostAdjustmentDetailCaptureTableModel, selectedItems?: number[] }) {
    switch (event.action) {
      case 'edit': {
        await this.editCostAdjustmentDetail(event.item!.id);
        break;
      }
      case 'bulk-delete': {
        if (event.selectedItems) {
          await this.deleteCostAdjustmentDetail(event.selectedItems);
        }
        break;
      }
    }
  }

  async openArticleSelectionModal() {
    if (!this.permissionUpdatable) {
      return this.toastHandlerService.showToast('warning', `El usuario no tiene permisos para la edición de ajustes.
        Comuníquese con el administrador del sistema.`);
    }
    if (!this.costAdjustmentModel.costAdjustmentId) {
      return this.toastHandlerService.showToast('warning', `El ajuste aún no se ha guardado.`);
    }
    this.filtersArticle = [];
    await Promise.all([
      this.getColumnsForArticle(),
      this.getArticlesByEntry()
    ]);
    this.articleSelectionModalComponent?.show();
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    this.filtersArticle = this.removeDuplicates(filters);
    this.filtersArticle.forEach((filter: any) => this.processFilter(filter));
    this.updateInputs();
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    await this.getCostAdjustmentDetailsForCapture();
  }

  async onPageChangeArticle(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.pageArticle = event.page;
    this.limitArticle = event.pageSize;
    this.columnSortArticle = event.sortField || null;
    this.ascendantArticle = event.sortOrder === 1;
    await this.getArticlesByEntry();
  }

  updateInputs() {
    this.inputsForArticlesSelection$.next({
      headers: this.headerArticle,
      data: this.dataArticle,
      showCheckbox: true,
      showRecover: true,
      isMultipleSelect: true,
      filters: this.filtersArticle,
    });
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filtersArticle = this.filtersArticle.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filtersArticle.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }
}
